import { FC, useEffect } from 'react';
import type { PhoneInputProps as ReactPhoneInputProps } from 'react-phone-number-input';
import { Field } from 'redux-form';

import PhoneInputField from 'inquiry/components/PhoneInputField';
import { parseEmptyStringToNull } from 'inquiry/lib/form';
import { useApply, useValidators } from 'inquiry/lib/jsonLogic';
import { useShouldShowServerError } from 'inquiry/lib/serverError';
import { InputPhoneNumberComponent, PhoneNumberFormat } from 'lib/interfaces/ui-components';
import isEmpty from 'lib/utils/formValidation/isEmpty';
import { UiComponentActionsApi } from 'verify/lib/interfaces/uiComponent';
import { Inquiry } from 'verify/models/inquiry';

interface InputPhoneNumberProps {
  component: InputPhoneNumberComponent;
  inquiry: Inquiry;
  inquiryLoading: boolean;
  actionsApi: UiComponentActionsApi;
}

const InputPhoneNumber: FC<InputPhoneNumberProps> = ({
  component,
  inquiry,
  inquiryLoading,
  actionsApi,
}: InputPhoneNumberProps) => {
  const {
    disabled,
    error,
    hidden,
    label,
    optional,
    placeholder,
    prefill,
    validations,
    'country-code': countryCode,
    'disable-country-code-selection': disableCountryCodeSelection,
    'phone-number-format': phoneNumberFormat,
  } = component.attributes;

  const formField = component.name;
  const shouldShowServerError = useShouldShowServerError(
    inquiry,
    inquiryLoading,
    formField,
    prefill,
  );

  const apply = useApply(formField, inquiry.attributes['next-step'].name);
  const validators = useValidators({ hidden, optional, validations });

  // TODO: figure out how to test this
  const isHidden = apply<boolean>(hidden);

  useEffect(() => {
    if (isHidden) {
      // Setting to '' removes the field value from form state entirely, unlike null which sets value to null.
      actionsApi.setField(formField, '');
    } else if (!isHidden && !isEmpty(prefill)) {
      actionsApi.setField(formField, prefill);
    }
  }, [isHidden]);

  if (isHidden) {
    return null;
  }

  // The international prop operates as a ternary boolean
  let international;
  if (phoneNumberFormat === PhoneNumberFormat.International) {
    international = true;
  } else if (phoneNumberFormat === PhoneNumberFormat.National) {
    international = false;
  } else {
    international = undefined;
  }

  return (
    <Field
      id={formField}
      name={formField}
      component={PhoneInputField}
      disabled={apply<boolean>(disabled)}
      parse={parseEmptyStringToNull}
      validate={validators}
      label={label}
      placeholder={placeholder}
      defaultCountry={countryCode}
      // react-phone-number-input prop to hide international country calling code on prefill
      // presence of country calling code causes input to be wiped when country is changed
      initialValueFormat={international ? null : 'national'}
      /**
       * Props to force selection of a single country
       * Note that props.country doesn't actually lock formatting to a single country
       */
      // Disables country select dropdown
      countrySelectProps={disableCountryCodeSelection ? LOCKED_COUNTRY_SELECT_PROPS : undefined}
      // Restricts format detection to single country
      countries={disableCountryCodeSelection ? [countryCode] : undefined}
      // Hides international numbers from format detection
      addInternationalOption={!disableCountryCodeSelection}
      // Sets phone number formatting, true for international, false for national, undefined for auto
      international={international}
      serverError={shouldShowServerError ? error : null}
      styles={component.styles}
    />
  );
};

export default InputPhoneNumber;

const LOCKED_COUNTRY_SELECT_PROPS: ReactPhoneInputProps['countrySelectProps'] = {
  disabled: true,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  arrowComponent: (): void => null,
};
